import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Switch,
  styled,
  CircularProgress,
  Alert,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { savePromoAPI, updatePromoAPI, getAdminId } from '../../operations/promoService';
import {
  Promo,
  PromoFormValues,
  FieldErrors,
  DiscountType,
  emptyPromoForm,
  promoToForm,
  validatePromoForm,
  buildCreatePayload,
  buildUpdatePayload,
  extractApiError,
} from './types';

// Controlled black/white switch (same look as components/common/BlackWhiteSwitch,
// but driven by props so it reflects/edits the promo's is_active).
const StatusSwitch = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#000',
    '& + .MuiSwitch-track': { backgroundColor: '#000' },
  },
  '& .MuiSwitch-switchBase': { color: '#ccc' },
  '& .MuiSwitch-track': { backgroundColor: '#ccc' },
}));

const errorText: React.CSSProperties = {
  display: 'block',
  color: '#e31a24',
  fontSize: 12,
  marginTop: 4,
};

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  promo?: Promo | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

const PromoFormDialog: React.FC<Props> = ({ open, mode, promo, onClose, onSaved }) => {
  const [values, setValues] = useState<PromoFormValues>(emptyPromoForm());
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValues(mode === 'edit' && promo ? promoToForm(promo) : emptyPromoForm());
    setErrors({});
    setServerErrors([]);
    setSubmitting(false);
  }, [open, mode, promo]);

  // For an "amount" discount, the flat value is the cap, so Max Discount mirrors
  // Discount Value (and the field is locked below). Keep them in sync.
  useEffect(() => {
    if (values.discount_type !== 'amount') return;
    setValues((prev) =>
      prev.max_discount === prev.discount_value ? prev : { ...prev, max_discount: prev.discount_value }
    );
  }, [values.discount_type, values.discount_value]);

  const set = <K extends keyof PromoFormValues>(name: K, value: PromoFormValues[K]) =>
    setValues((prev) => ({ ...prev, [name]: value } as PromoFormValues));

  const isEdit = mode === 'edit';

  const handleSubmit = async () => {
    const fieldErrors = validatePromoForm(values, mode);
    setErrors(fieldErrors);
    setServerErrors([]);
    if (Object.keys(fieldErrors).length > 0) return;

    setSubmitting(true);
    try {
      const res =
        mode === 'create'
          ? await savePromoAPI(buildCreatePayload(values, getAdminId()))
          : await updatePromoAPI(promo!.promo_id, buildUpdatePayload(values, getAdminId()));

      const status = res?.status;
      if (status === 200) {
        onSaved(res?.message || (isEdit ? 'Promo updated' : 'Promo created'));
        return;
      }
      if (status === 400 && Array.isArray(res?.data)) {
        setServerErrors((res.data as unknown[]).filter((x): x is string => typeof x === 'string'));
        return;
      }
      if (status === 409) {
        setErrors((p) => ({ ...p, promo_code: res?.message || 'Promo code already exists' }));
        setServerErrors([res?.message || 'A promo with this code already exists']);
        return;
      }
      setServerErrors([res?.message || 'Something went wrong']);
    } catch (err) {
      const { message, errors: errs } = extractApiError(err);
      setServerErrors(errs.length ? errs : [message]);
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (
    name: keyof PromoFormValues,
    label: string,
    opts: { type?: string; readOnly?: boolean; placeholder?: string } = {}
  ) => {
    const { type = 'text', readOnly = false, placeholder } = opts;
    return (
      <div className="form-control inputFieldWrap" key={name}>
        <label>{label}</label>
        <input
          type={type}
          className="inputField"
          value={values[name] as string}
          onChange={(e) => set(name, e.target.value as PromoFormValues[typeof name])}
          readOnly={readOnly}
          placeholder={placeholder}
          style={readOnly ? { backgroundColor: '#f7fcff', cursor: 'not-allowed' } : undefined}
        />
        {errors[name] && <span style={errorText}>{errors[name]}</span>}
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <CancelIcon onClick={onClose} className="closeModal" />
      <DialogTitle>{isEdit ? 'Edit Promo Code' : 'Create Promo Code'}</DialogTitle>
      <DialogContent dividers>
        <Box className="modalFormWrap">
          {serverErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverErrors.length === 1 ? (
                serverErrors[0]
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {serverErrors.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}

          <Box
            component="form"
            className="modalForm"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Box className="feeModalForm promoPopupWrap">
              <Typography variant="subtitle1" gutterBottom>
                Details
              </Typography>
              <Box className="feeInputFieldWrap">
                {renderInput('promo_code', 'Promo Code', {
                  readOnly: isEdit,
                  placeholder: 'e.g. SUMMER10',
                })}
                {renderInput('title', 'Title', { placeholder: 'Short name (≤ 36 chars)' })}
                {renderInput('description', 'Description', { placeholder: 'Text (≤ 255 chars)' })}
              </Box>

              <Typography variant="subtitle1" gutterBottom>
                Discount
              </Typography>
              <Box className="feeInputFieldWrap">
                <div className="form-control inputFieldWrap">
                  <label>Discount Type</label>
                  <FormControl sx={{ minWidth: 265 }}>
                    <Select
                      className="promodropdown"
                      value={values.discount_type}
                      onChange={(e) => set('discount_type', e.target.value as DiscountType)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected)
                          return <span style={{ color: '#888' }}>Select Discount Type</span>;
                        return selected === 'amount' ? 'Amount' : 'Percentage';
                      }}
                    >
                      <MenuItem value="">Select Discount Type</MenuItem>
                      <MenuItem value="amount">Amount</MenuItem>
                      <MenuItem value="percentage">Percentage</MenuItem>
                    </Select>
                  </FormControl>
                  {errors.discount_type && <span style={errorText}>{errors.discount_type}</span>}
                </div>
                {renderInput('discount_value', 'Discount Value', { type: 'number' })}
                {renderInput('min_price', 'Minimum Order', { type: 'number' })}
                {renderInput('max_discount', 'Maximum Discount', {
                  type: 'number',
                  readOnly: values.discount_type === 'amount',
                  placeholder:
                    values.discount_type === 'amount' ? 'Same as discount value' : 'blank = no cap',
                })}
              </Box>

              <Typography variant="subtitle1" gutterBottom>
                Schedule
              </Typography>
              <Box className="feeInputFieldWrap">
                {renderInput('start_date', 'Start Date', { type: 'datetime-local' })}
                {renderInput('end_date', 'End Date', { type: 'datetime-local' })}
              </Box>

              <Typography variant="subtitle1" gutterBottom>
                Limits
              </Typography>
              <Box className="feeInputFieldWrap">
                {renderInput('usage_limit', 'Usage Limit', { type: 'number' })}
                {renderInput('per_user_usage_count', 'Per User Limit', { type: 'number' })}
              </Box>
            </Box>
          </Box>

          <div className="statusWrap">
            <label>Status</label>
            <Box display="flex" alignItems="center" gap={2}>
              <StatusSwitch
                checked={values.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
              />
              <Typography variant="body1" color={values.is_active ? 'green' : '#888'}>
                {values.is_active ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          </div>

          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mt={2}>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={submitting}
              sx={{ minWidth: 120, height: 40 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{
                minWidth: 120,
                height: 40,
                backgroundColor: '#272727',
                color: '#fff',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#111' },
              }}
            >
              {isEdit ? 'Update' : 'Save'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PromoFormDialog;
